// Web replacement for mobile's react-native-audio-recorder-player. Records
// with MediaRecorder into whatever container the browser supports; the
// backend pipeline content-sniffs with ffprobe, and Whisper accepts
// webm/ogg/mp4 alike, so no backend change is needed.
import {useCallback, useEffect, useRef, useState} from 'react';

export type RecorderStatus = 'idle' | 'recording' | 'paused';

export interface RecordingResult {
  blob: Blob;
  /** File extension for the Storage path (webm | ogg | m4a). */
  ext: string;
  /** Elapsed recording time in seconds (pause-aware). */
  duration: number;
}

interface MimeChoice {
  mime: string;
  ext: string;
}

// Preference order: Safari records AAC-in-MP4 (uploaded as .m4a, matching
// mobile), Chrome/Firefox record Opus in webm/ogg.
const MIME_CHOICES: MimeChoice[] = [
  {mime: 'audio/mp4', ext: 'm4a'},
  {mime: 'audio/webm;codecs=opus', ext: 'webm'},
  {mime: 'audio/webm', ext: 'webm'},
  {mime: 'audio/ogg;codecs=opus', ext: 'ogg'},
  {mime: 'audio/ogg', ext: 'ogg'},
];

const pickMime = (): MimeChoice | null => {
  if (typeof MediaRecorder === 'undefined') {
    return null;
  }
  return (
    MIME_CHOICES.find(choice => MediaRecorder.isTypeSupported(choice.mime)) ??
    null
  );
};

export const recordingSupported = (): boolean =>
  typeof MediaRecorder !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  pickMime() !== null;

export class MicDeniedError extends Error {}

export function useRecorder() {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const extRef = useRef('webm');
  const startedAtRef = useRef(0);
  const accumulatedRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  const currentElapsed = () =>
    accumulatedRef.current +
    (startedAtRef.current ? (Date.now() - startedAtRef.current) / 1000 : 0);

  const stopMeters = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  };

  const cleanup = useCallback(() => {
    stopMeters();
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    startedAtRef.current = 0;
    accumulatedRef.current = 0;
    setStatus('idle');
    setElapsed(0);
    setLevel(0);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startMeters = (stream: MediaStream) => {
    timerRef.current = window.setInterval(
      () => setElapsed(currentElapsed()),
      250,
    );
    try {
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        setLevel(Math.min(1, Math.sqrt(sum / data.length) * 3));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // Level meter is cosmetic — recording works without it.
    }
  };

  const start = useCallback(async () => {
    const choice = pickMime();
    if (!choice) {
      throw new Error('unsupported');
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({audio: true});
    } catch (e) {
      throw new MicDeniedError(String(e));
    }
    streamRef.current = stream;
    extRef.current = choice.ext;
    chunksRef.current = [];
    accumulatedRef.current = 0;

    const recorder = new MediaRecorder(stream, {mimeType: choice.mime});
    recorderRef.current = recorder;
    recorder.ondataavailable = event => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.start(1000);
    startedAtRef.current = Date.now();
    setStatus('recording');
    startMeters(stream);
  }, []);

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder?.state === 'recording') {
      recorder.pause();
      accumulatedRef.current = currentElapsed();
      startedAtRef.current = 0;
      setStatus('paused');
    }
  }, []);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder?.state === 'paused') {
      recorder.resume();
      startedAtRef.current = Date.now();
      setStatus('recording');
    }
  }, []);

  /** Stops recording and resolves with the final blob. */
  const stop = useCallback((): Promise<RecordingResult> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      cleanup();
      return Promise.reject(new Error('Not recording'));
    }
    const duration = Math.round(currentElapsed());
    return new Promise(resolve => {
      recorder.onstop = () => {
        const mime = recorder.mimeType.split(';')[0] || 'audio/webm';
        const blob = new Blob(chunksRef.current, {type: mime});
        const ext = extRef.current;
        cleanup();
        resolve({blob, ext, duration});
      };
      recorder.stop();
    });
  }, [cleanup]);

  /** Discards the recording entirely. */
  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.stop();
    }
    cleanup();
  }, [cleanup]);

  return {status, elapsed, level, start, pause, resume, stop, cancel};
}
